import { useState, useEffect, useCallback } from 'react';
import { getOrders, createOrder, updateOrderStatus, getMenuItems, getRooms } from '../lib/api';
import { OrderFormDialog } from '../components/orders/OrderFormDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, ClipboardList, Search, Filter, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const orderStatuses = ['pending', 'preparing', 'ready', 'delivered', 'cancelled'];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [orderItems, setOrderItems] = useState([]);
  const [formData, setFormData] = useState({
    room_number: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, menuRes, roomsRes] = await Promise.all([
        getOrders(filterStatus || undefined),
        getMenuItems(undefined, true), // Only available items
        getRooms('occupied')
      ]);
      setOrders(ordersRes.data);
      setMenuItems(menuRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (orderItems.length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    try {
      await createOrder({
        room_number: formData.room_number || null,
        notes: formData.notes || null,
        items: orderItems.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity
        }))
      });
      toast.success('Order created successfully');
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const addItemToOrder = (menuItem) => {
    const existing = orderItems.find(item => item.id === menuItem.id);
    if (existing) {
      setOrderItems(orderItems.map(item => 
        item.id === menuItem.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setOrderItems([...orderItems, { ...menuItem, quantity: 1 }]);
    }
  };

  const updateItemQuantity = (itemId, delta) => {
    setOrderItems(orderItems.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeItem = (itemId) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
  };

  const resetForm = () => {
    setFormData({
      room_number: '',
      notes: ''
    });
    setOrderItems([]);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const filteredOrders = orders.filter(order => {
    const roomNum = order.room_number?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return roomNum.includes(query);
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const orderTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const getNextStatus = (currentStatus) => {
    const flow = { pending: 'preparing', preparing: 'ready', ready: 'delivered' };
    return flow[currentStatus];
  };

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Orders</h1>
          <p className="text-stone-500 mt-1">Track and manage food & beverage orders</p>
        </div>
        <Button 
          onClick={openAddModal}
          className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm"
          data-testid="add-order-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search by room..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-orders-input"
          />
        </div>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 border-stone-300" data-testid="filter-order-status">
            <Filter className="w-4 h-4 mr-2 text-stone-400" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            {orderStatuses.map(status => (
              <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No orders found</p>
            <Button 
              onClick={openAddModal}
              variant="outline"
              className="mt-4"
            >
              Create your first order
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="font-semibold text-stone-700">Time</TableHead>
                <TableHead className="font-semibold text-stone-700">Room</TableHead>
                <TableHead className="font-semibold text-stone-700">Items</TableHead>
                <TableHead className="font-semibold text-stone-700">Total</TableHead>
                <TableHead className="font-semibold text-stone-700">Status</TableHead>
                <TableHead className="font-semibold text-stone-700 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order, index) => (
                <TableRow 
                  key={order.id}
                  className="table-row-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`order-row-${order.id}`}
                >
                  <TableCell className="text-stone-600">{formatTime(order.created_at)}</TableCell>
                  <TableCell className="font-medium text-stone-900">
                    {order.room_number ? `Room ${order.room_number}` : 'Walk-in'}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px]">
                      {order.items?.map((item, i) => (
                        <span key={i} className="text-sm text-stone-600">
                          {item.quantity}x {item.menu_item_name}
                          {i < order.items.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-stone-900">{formatCurrency(order.total_amount)}</TableCell>
                  <TableCell>
                    <span className={`status-badge status-${order.status}`}>
                      {order.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {getNextStatus(order.status) && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(order.id, getNextStatus(order.status))}
                        className="h-8 px-3 text-stone-700 hover:bg-stone-50 border-stone-300"
                        data-testid={`update-order-status-${order.id}`}
                      >
                        Mark {getNextStatus(order.status)}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* New Order Modal */}
      <OrderFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        rooms={rooms}
        menuItems={menuItems}
        orderItems={orderItems}
        addItemToOrder={addItemToOrder}
        updateItemQuantity={updateItemQuantity}
        orderTotal={orderTotal}
      />
    </div>
  );
}
